import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Loader2 } from 'lucide-react';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { adminApi, subscriptionsApi, ApiError } from '../../api/client';
import { cn } from '../../utils/cn';
import { ONBOARDING_PATH } from '../../utils/onboarding';

const useIsMimicking = () => {
  const [isMimicking, setIsMimicking] = useState(false);

  useEffect(() => {
    const checkMimicState = () => {
      const originalToken = localStorage.getItem('originalAdminToken');
      const mimickedUserData = localStorage.getItem('mimickedUser');
      setIsMimicking(!!originalToken && !!mimickedUserData);
    };

    checkMimicState();
    window.addEventListener('storage', checkMimicState);
    return () => window.removeEventListener('storage', checkMimicState);
  }, []);

  return isMimicking;
};

function derivePlanFromSubscriptionResponse(response: any): {
  displayName: string;
  name: string;
} | null {
  if (!response) return null;
  if (response.plan) {
    return {
      displayName: response.plan.displayName || response.plan.name || 'Plan',
      name: response.plan.name || 'PLAN',
    };
  }
  if (response.usage?.plan) {
    return {
      displayName: response.usage.plan.displayName || response.usage.plan.name || 'Free',
      name: response.usage.plan.name || 'FREE',
    };
  }
  if (response.subscription) {
    const planId = response.subscription.planId || response.subscription.planName;
    if (planId) {
      const upper = String(planId).toUpperCase();
      if (upper.includes('STARTER')) {
        return { name: 'STARTER', displayName: 'Starter' };
      }
      if (upper.includes('PROFESSIONAL')) {
        return { name: 'PROFESSIONAL', displayName: 'Professional' };
      }
    }
    return { name: 'FREE', displayName: 'Free' };
  }
  return { name: 'FREE', displayName: 'Free' };
}

export const AccountSettings: React.FC = () => {
  const navigate = useNavigate();
  const isMimicking = useIsMimicking();
  const { user, loading: authLoading, isAuthenticated, isAdmin, updateUser } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const [planSummary, setPlanSummary] = useState<{ displayName: string; name: string } | null>(
    null
  );
  const [subscriptionStatus, setSubscriptionStatus] = useState<Record<string, unknown> | null>(
    null
  );
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingMessage, setBillingMessage] = useState<string | null>(null);
  const [requiresPaymentSetup, setRequiresPaymentSetup] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isAuthenticated || authLoading) return;
      setSubscriptionLoading(true);
      try {
        const response = await subscriptionsApi.getCurrentSubscription();
        if (cancelled) return;
        if (response?.subscription) {
          setSubscriptionStatus(response.subscription);
        }
        setPlanSummary(derivePlanFromSubscriptionResponse(response));
      } catch {
        if (!cancelled) {
          setPlanSummary({ name: 'FREE', displayName: 'Free' });
          setSubscriptionStatus(null);
        }
      } finally {
        if (!cancelled) setSubscriptionLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    let cancelled = false;
    const loadOnboarding = async () => {
      if (!isAuthenticated || authLoading) return;
      if (isAdmin) {
        setRequiresPaymentSetup(false);
        setOnboardingLoading(false);
        return;
      }
      setOnboardingLoading(true);
      try {
        const status = await subscriptionsApi.getOnboardingStatus();
        if (!cancelled) {
          setRequiresPaymentSetup(status.requiresPaymentSetup);
        }
      } catch {
        if (!cancelled) {
          setRequiresPaymentSetup(true);
        }
      } finally {
        if (!cancelled) {
          setOnboardingLoading(false);
        }
      }
    };
    loadOnboarding();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authLoading, isAdmin]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setProfileError(null);
    setProfileSuccess(false);

    const nameTrim = name.trim();
    const emailTrim = email.trim();

    const nameParsed = z.string().min(2, 'Name must be at least 2 characters').safeParse(nameTrim);
    if (!nameParsed.success) {
      setProfileError(nameParsed.error.errors[0]?.message || 'Invalid name');
      return;
    }

    const emailParsed = z.string().email('Invalid email address').safeParse(emailTrim);
    if (!emailParsed.success) {
      setProfileError(emailParsed.error.errors[0]?.message || 'Invalid email');
      return;
    }

    let passwordToSend: string | undefined;
    if (password.length > 0 || confirmPassword.length > 0) {
      if (password.length < 8) {
        setProfileError('New password must be at least 8 characters');
        return;
      }
      if (password !== confirmPassword) {
        setProfileError('Passwords do not match');
        return;
      }
      passwordToSend = password;
    }

    setSavingProfile(true);
    try {
      const payload: { name: string; email: string; password?: string } = {
        name: nameTrim,
        email: emailTrim,
      };
      if (passwordToSend) payload.password = passwordToSend;

      const updated = await adminApi.updateUser(user.id, payload);
      updateUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      setPassword('');
      setConfirmPassword('');
      setProfileSuccess(true);
      window.setTimeout(() => setProfileSuccess(false), 4000);
    } catch (err) {
      if (err instanceof ApiError) {
        setProfileError(err.message);
      } else if (err instanceof Error) {
        setProfileError(err.message);
      } else {
        setProfileError('Could not save your profile');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingMessage(null);
    setPortalLoading(true);
    try {
      const { portalUrl } = await subscriptionsApi.createPortalSession({
        returnUrl: `${window.location.origin}/account`,
      });
      window.location.href = portalUrl;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setBillingMessage(
          'No Stripe billing profile yet. Subscribe to a paid plan first, then you can update your payment method in the billing portal.'
        );
      } else if (err instanceof ApiError) {
        setBillingMessage(err.message);
      } else {
        setBillingMessage('Unable to open billing portal. Try again later.');
      }
      setPortalLoading(false);
    }
  };

  if (authLoading || (!isAuthenticated && !authLoading)) {
    return (
      <div
        className={cn(
          'page-container flex items-center justify-center',
          isMimicking && 'mt-[44px]'
        )}
      >
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 spinner" />
          <p className="text-gray-600 font-medium">Loading account…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const backLinkPath = requiresPaymentSetup ? ONBOARDING_PATH : '/dashboard';
  const backLinkLabel = requiresPaymentSetup ? 'Back to setup' : 'Back to dashboard';

  return (
    <div className={cn('page-container min-h-screen bg-gray-50', isMimicking && 'mt-[44px]')}>
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Link
            to={backLinkPath}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {backLinkLabel}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account settings</h1>
          <p className="mt-1 text-sm text-gray-600">
            Update your profile and manage your subscription.
          </p>
        </div>

        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile</h2>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label htmlFor="account-name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                id="account-name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={e => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="account-email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="account-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="account-password" className="block text-sm font-medium text-gray-700">
                New password
              </label>
              <input
                id="account-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Leave blank to keep current password"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">At least 8 characters if you change it.</p>
            </div>
            <div>
              <label
                htmlFor="account-confirm-password"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm new password
              </label>
              <input
                id="account-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            {profileError && (
              <p className="text-sm text-red-600" role="alert">
                {profileError}
              </p>
            )}
            {profileSuccess && (
              <p className="text-sm text-green-600" role="status">
                Profile saved successfully.
              </p>
            )}

            <button
              type="submit"
              disabled={savingProfile}
              className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {savingProfile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save profile'
              )}
            </button>
          </form>
        </section>

        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Plan & billing</h2>

          {onboardingLoading ? (
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading account status…
            </div>
          ) : requiresPaymentSetup ? (
            <div className="space-y-4">
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
                Add a payment method to finish setup and access the website builder. Your profile
                settings above can still be updated anytime.
              </div>
              <div className="rounded-md bg-gray-50 px-4 py-3 border border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Account status
                </p>
                <p className="text-base font-semibold text-gray-900 mt-1">Pending setup</p>
              </div>
              <Link
                to={ONBOARDING_PATH}
                className="inline-flex justify-center items-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
              >
                Complete setup
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Change your plan or open Stripe to update payment methods and invoices.
              </p>

              {subscriptionLoading ? (
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading subscription…
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-md bg-gray-50 px-4 py-3 border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Current plan
                    </p>
                    <p className="text-base font-semibold text-gray-900 mt-1">
                      {planSummary?.displayName ?? 'Free'} Plan
                    </p>
                    {subscriptionStatus?.status != null && (
                      <p className="text-xs text-gray-500 mt-1 capitalize">
                        Status: {String(subscriptionStatus.status)}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/pricing"
                      className="inline-flex justify-center items-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                    >
                      Change plan
                    </Link>
                    <button
                      type="button"
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                      className="inline-flex justify-center items-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
                    >
                      {portalLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Opening…
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Manage billing
                        </>
                      )}
                    </button>
                  </div>

                  {billingMessage && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                      {billingMessage}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
};
